import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Container, Form, Button } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Loader } from '@googlemaps/js-api-loader';
import { recaptchaSize } from '../../helpers/common';
import { handleLargeFilesIfRequired, getBase64, submitFormData } from '../../helpers/form-api';
import InputFile from '../InputFile';
import ThankYou, { ThankYouType } from './partials/ThankYou';

const recaptchaKey = process.env.GATSBY_GOOGLE_RECAPTCHA_KEY || '';
const googleMapsKey = process.env.GATSBY_GOOGLE_MAPS_KEY || '';

const RegisteredElectricalContractorsForm = () => {
  const initialFormData = {
    lew_full_name: '',
    rec_number: '',
    email_address: '',
    phone_number: '',
    national_meter_identifier_nmi: '',
    meter_number: '',
    street_address: '',
    site_access_clear_and_no_hazards: '',
    is_the_property_currently_occupied: '',
    description_of_works: '',
    attachment1: '',
    attachment2: '',
    attachment3: '',
    attachment4: '',
    attachment5: '',
    attachment6: '',
    attachment7: '',
    attachment8: '',
    attachment9: '',
    attachment10: '',
  };

  const initialFormErrors = {
    lew_full_name: false,
    rec_number: false,
    email_address: false,
    phone_number: false,
    national_meter_identifier_nmi: false,
    meter_number: false,
    street_address: false,
    attachment1: false,
    attachment2: false,
    attachment3: false,
  };

  const recaptchaRef = useRef(null);
  const addressRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState(false);
  const [success, setSuccess] = useState(false);
  const [emailData, setEmailData] = useState(undefined);
  const [formErrors, setFormErrors] = useState(initialFormErrors);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [googleApiLoaded, setGoogleApiLoaded] = useState(false);

  const fileInputs = {
    fileInputRef1: useRef(null),
    fileInputWrapperRef1: useRef(null),
    fileInputRef2: useRef(null),
    fileInputWrapperRef2: useRef(null),
    fileInputRef3: useRef(null),
    fileInputWrapperRef3: useRef(null),
    fileInputRef4: useRef(null),
    fileInputWrapperRef4: useRef(null),
    fileInputRef5: useRef(null),
    fileInputWrapperRef5: useRef(null),
    fileInputRef6: useRef(null),
    fileInputWrapperRef6: useRef(null),
    fileInputRef7: useRef(null),
    fileInputWrapperRef7: useRef(null),
    fileInputRef8: useRef(null),
    fileInputWrapperRef8: useRef(null),
    fileInputRef9: useRef(null),
    fileInputWrapperRef9: useRef(null),
    fileInputRef10: useRef(null),
    fileInputWrapperRef10: useRef(null),
  };

  useEffect(() => {
    if (Object.keys(formErrors).every((key) => !formErrors[key]) && captcha) {
      setError(false);
    }
  }, [formErrors, captcha]);

  useEffect(() => {
    if (!googleApiLoaded) {
      const loader = new Loader({
        apiKey: googleMapsKey,
        version: 'weekly',
      });

      loader.importLibrary('places').then((places) => {
        addressRef.current = new places.Autocomplete(document.getElementById('form-street-address'), {
          componentRestrictions: { country: 'AU' },
        });

        setGoogleApiLoaded(true);
      });
    }
  }, [googleApiLoaded, addressRef]);

  useEffect(() => {
    if (!googleApiLoaded) {
      return;
    }

    if (addressRef && addressRef.current) {
      const addressRefEl = addressRef.current;
      const handlePlaceChanged = () => {
        const place = addressRefEl.getPlace();
        setFormData({
          ...formData,
          street_address: place.formatted_address,
        });
        setFormErrors({
          ...formErrors,
          street_address: false,
        });
      };

      const listener = addressRefEl.addListener('place_changed', handlePlaceChanged);

      return () => {
        listener.remove();
      };
    }
  }, [formData, formErrors, googleApiLoaded]);

  const handleInputChange = (event) => {
    const target = event.currentTarget;
    const value = target.value;
    const name = target.name;
    const phoneRegex = /[^0-9]/g;
    const nmiRegex = /[^A-Za-z0-9]/g;

    let formattedValue;
    switch (name) {
      case 'phone_number':
        formattedValue = value.replaceAll(phoneRegex, '');
        break;
      case 'national_meter_identifier_nmi':
        formattedValue = value.replaceAll(nmiRegex, '');
        break;
      default:
        formattedValue = value;
        break;
    }

    validateForm(name, formattedValue);
    setFormData({
      ...formData,
      [name]: formattedValue,
    });
  };

  const handleSelectChange = (event, { name, value }) => {
    validateForm(name, value);
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCaptcha = (value) => {
    setCaptcha(value);
  };

  const validateForm = (fieldName, value) => {
    let error = false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    switch (fieldName) {
      case 'national_meter_identifier_nmi':
        if (value.trim() && value.length !== 11) {
          error = 'Must be 11 characters in length';
        }
        break;
      case 'email_address':
        if (value.trim() && !emailRegex.test(value)) {
          error = 'Email is not valid';
        }
        break;
      case 'street_address':
        if (!value) {
          error = 'Must select a valid address';
        }
        break;
      case 'attachment1':
      case 'attachment2':
      case 'attachment3':
      case 'attachment4':
      case 'attachment5':
      case 'attachment6':
      case 'attachment7':
      case 'attachment8':
      case 'attachment9':
      case 'attachment10':
        if (
          Array(10)
            .fill(0)
            .map((_value, i) => fileInputs[`fileInputRef${i + 1}`])
            .every((fileInputRef) => !fileInputRef.current?.files?.length)
        ) {
          error = 'Must upload at least one attachment';
        }
        break;
      case 'site_access_clear_and_no_hazards':
        if (value === 'no') {
          error =
            'We cannot accept any reconnection jobs without confirming clear, hazard free access to the property. Please confirm this to continue.';
        }
        break;
    }

    setFormErrors({
      ...formErrors,
      [fieldName]: error,
    });

    return error;
  };

  const handleFocus = (event) => {
    event.target.setAttribute('autocomplete', 'disabled');
  };

  const handleSubmit = async () => {
    let hasErrors = false;
    Object.keys(formErrors).every((field) => {
      const result = validateForm(field, formData[field]);
      if (result !== false) {
        hasErrors = true;
        return false; // Error has been found
      }
      return true; // No error on this field
    });

    if (hasErrors) {
      setError('Must resolve all form errors before submitting');
      return;
    }

    try {
      setError(false);
      setSuccess(false);
      setSubmitting(true);

      if (captcha) {
        const attachments = {};
        for (let i = 1; i <= 10; i++) {
          const file = fileInputs[`fileInputRef${i}`].current.files[0];

          if (!file) {
            continue;
          }

          attachments[`attachment${i}`] = {
            content: await getBase64(file),
            name: file.name,
          };

          const fileSize = file.size / 1024 / 1024; // in MB

          if (fileSize > 10) {
            setSuccess(false);
            setFormErrors({
              ...formErrors,
              [`attachment${i}`]: 'File size exceeds 10 MB.',
            });
            setSubmitting(false);
            return;
          }
        }

        const dataToSubmit = {
          ...formData,
          ...await handleLargeFilesIfRequired(attachments),
          captchaResponse: captcha,
        };

        const response = await submitFormData('registered_electrical_contractors', dataToSubmit);

        if (response.status !== 200) {
          setSuccess(false);
          setError(response.error);
          setSubmitting(false);
          setCaptcha(null);
        } else {
          setSuccess('Success');
          setEmailData(response.emailData);
          setError(false);
          setSubmitting(false);
          setCaptcha(null);
          setFormData(initialFormData);

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'registered_electrical_contractors_form_submission',
            data: formData,
          });
        }

        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }

        for (let i = 1; i <= 10; i++) {
          fileInputs[`fileInputWrapperRef${i}`].current && fileInputs[`fileInputWrapperRef${i}`].current.clear();
        }
      } else {
        setError('Please tick ReCAPTCHA');
        setSuccess(false);
        setSubmitting(false);
      }
    } catch {
      setError('Something went wrong, we have been notified of the error, please try again later.');
      setSuccess(false);
      setSubmitting(false);
    }
  };

  const submitText = () => {
    if (submitting) {
      return 'Submitting...';
    }
    return 'Submit';
  };

  if (success) {
    return <ThankYou emailData={emailData} type={ThankYouType.REGISTERED_ELECTRICAL_CONTRACTORS} />;
  }

  return (
    <section className="registered-electrical-contractors-form">
      <Container>
        <div className="grid-wrapper">
          <div className="form-container">
            <Form onSubmit={handleSubmit} autoComplete="chrome-off">
              <Form.Input
                label="LEW full name"
                required
                name="lew_full_name"
                type="text"
                value={formData.lew_full_name}
                onChange={handleInputChange}
                id="form-lew-full-name"
              />
              <Form.Input
                label="REC number"
                required
                name="rec_number"
                type="text"
                value={formData.rec_number}
                onChange={handleInputChange}
                id="form-rec-number"
              />
              <Form.Input
                label="Email address"
                name="email_address"
                type="email"
                value={formData.email_address}
                onChange={handleInputChange}
                id="form-email-address"
                error={
                  formErrors.email_address
                    ? {
                        content: formErrors.email_address,
                      }
                    : false
                }
              />
              <Form.Input
                label="Phone number"
                required
                name="phone_number"
                type="text"
                value={formData.phone_number}
                onChange={handleInputChange}
                id="form-phone-number"
                error={
                  formErrors.phone_number
                    ? {
                        content: formErrors.phone_number,
                      }
                    : false
                }
              />
              <Form.Input
                label="National Meter Identifier (NMI)"
                name="national_meter_identifier_nmi"
                type="text"
                value={formData.national_meter_identifier_nmi}
                onChange={handleInputChange}
                id="form-national-meter-identifier-nmi"
                error={formErrors.national_meter_identifier_nmi}
              />
              <Form.Input
                label="Meter number"
                required
                name="meter_number"
                type="text"
                value={formData.meter_number}
                onChange={handleInputChange}
                id="form-meter-number"
              />
              <Form.Input
                label="Street address"
                onFocus={handleFocus}
                onChange={handleInputChange}
                name="street_address"
                type="text"
                id="form-street-address"
                error={formErrors.street_address}
                value={formData.street_address}
                required
              />
              <Form.Select
                label="Site Access Clear and no hazards?"
                required
                options={[
                  { text: 'Yes', value: 'yes', key: 'yes' },
                  { text: 'No', value: 'no', key: 'no' },
                ]}
                placeholder="Please choose"
                value={formData.site_access_clear_and_no_hazards}
                name="site_access_clear_and_no_hazards"
                onChange={handleSelectChange}
                id="form-site-access-clear-and-no-hazards"
                error={formErrors.site_access_clear_and_no_hazards}
              />
              <Form.Select
                label="Is the property currently occupied?"
                required
                options={[
                  { text: 'Yes', value: 'yes', key: 'yes' },
                  { text: 'No', value: 'no', key: 'no' },
                ]}
                placeholder="Please choose"
                value={formData.is_the_property_currently_occupied}
                name="is_the_property_currently_occupied"
                onChange={handleSelectChange}
                id="form-ready-for-reconnection-now"
                error={formErrors.is_the_property_currently_occupied}
              />
              {formData.is_the_property_currently_occupied === 'no' && <p>This job may be held over and dispatched the next day</p>}
              <Form.TextArea
                label="Description of works"
                name="description_of_works"
                value={formData.description_of_works}
                onChange={handleInputChange}
                id="form-description-of-works"
              />

              <div className="grid-wrapper rec-form__attachments-grid">
                <div className="rec-form__attachment-description file-input__restrictions">
                  <div className="field">
                    <label>Attachments</label>
                  </div>
                  <small>
                    Please ensure any attachments are clear and legible.
                    <br />
                    Max. filesize 10MB. Please upload only pdf, doc, docx, xls, xslx, pages, numbers, jpg, tiff, bmp, png, txt, csv, rtf,
                    odt or ods files. Note that if form submission fails, please re-upload attachment.
                  </small>
                </div>

                {Array(10)
                  .fill(0)
                  .map((_value, index) => {
                    return (
                      <div key={index} className="rec-form__attachment-wrapper">
                        <Form.Field required={index === 0}>
                          <InputFile
                            label={`Attachment ${index + 1}`}
                            name={`attachment${index + 1}`}
                            text="No file chosen"
                            maxFileSize={10}
                            refProp={fileInputs[`fileInputRef${index + 1}`]}
                            ref={fileInputs[`fileInputWrapperRef${index + 1}`]}
                            error={formErrors[`attachment${index + 1}`]}
                            required
                          />
                        </Form.Field>
                      </div>
                    );
                  })}
              </div>

              <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaKey} onChange={handleCaptcha} size={recaptchaSize()} />
              <Button className="form--submit-btn" disabled={submitting} loading={submitting}>
                {submitText()}
              </Button>
              {success ? <div className="success-message">{success}</div> : null}
              {error ? <div className="error-message">{error}</div> : null}
            </Form>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default RegisteredElectricalContractorsForm;
